import { useQuery, useMutation } from 'convex/react';
import { api } from '../../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: components in src/components/insurance/ require three levels up
import type { Id } from '../../../convex/_generated/dataModel'; // DEEP_IMPORT_JUSTIFIED: standard convex _generated import pattern
import { useAssignmentDialog } from '@/hooks/useAssignmentDialog';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Check, ChevronsUpDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';

interface IAssignmentsListProps {
  planId: Id<'benefitPlans'>;
  orgId: Id<'organisations'>;
}

export function AssignmentsList({ planId, orgId }: IAssignmentsListProps) {
  const assignments = useQuery(api.insurance.benefitAssignments.listByPlan, { planId });
  const policies = useQuery(api.insurance.policies.list, { orgId });
  const assignPlan = useMutation(api.insurance.benefitAssignments.assignPlanToPolicy);
  const unassignPlan = useMutation(api.insurance.benefitAssignments.unassign);
  const reassignPlan = useMutation(api.insurance.benefitReassign.reassign);

  const {
    dialogOpen,
    openDialog,
    closeDialog,
    selectedPolicyId,
    setSelectedPolicyId,
    confirmUnassignId,
    requestUnassign,
    cancelUnassign,
    isAssigning,
    setIsAssigning,
  } = useAssignmentDialog();

  const existingAssignment = useQuery(
    api.insurance.benefitReassign.getAssignmentForPolicy,
    selectedPolicyId ? { policyId: selectedPolicyId as Id<'insurancePolicies'> } : 'skip' // AS_JUSTIFIED: one-time Id cast from combobox value string
  );

  const [comboboxOpen, setComboboxOpen] = useState(false);

  const policyMap = new Map((policies ?? []).map(p => [p._id, p]));
  const assignedPolicyIds = new Set((assignments ?? []).map(a => a.policyId));
  const allPoliciesExceptCurrentPlan = (policies ?? []).filter(p => !assignedPolicyIds.has(p._id));
  const hasExistingOtherPlan = existingAssignment && existingAssignment.planId !== planId;

  const selectedPolicy = (policies ?? []).find(p => p._id === selectedPolicyId);
  const selectedDisplayText = selectedPolicy
    ? `${selectedPolicy.policyNumber}${selectedPolicy.customerName ? ` — ${selectedPolicy.customerName}` : ''}`
    : null;

  async function handleAssign() {
    if (!selectedPolicyId) return;
    setIsAssigning(true);
    try {
      if (hasExistingOtherPlan) {
        await reassignPlan({
          orgId,
          policyId: selectedPolicyId as Id<'insurancePolicies'>, // AS_JUSTIFIED: one-time Id cast from combobox value string
          newPlanId: planId,
        });
      } else {
        await assignPlan({
          orgId,
          planId,
          policyId: selectedPolicyId as Id<'insurancePolicies'>, // AS_JUSTIFIED: one-time Id cast from combobox value string
        });
      }
      closeDialog();
    } finally {
      setIsAssigning(false);
    }
  }

  async function handleUnassign() {
    if (!confirmUnassignId) return;
    await unassignPlan({ assignmentId: confirmUnassignId });
    cancelUnassign();
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {(assignments ?? []).length} polic{(assignments ?? []).length === 1 ? 'y' : 'ies'}{' '}
          assigned
        </p>
        <Button size="sm" variant="outline" onClick={openDialog}>
          <Plus className="mr-1.5 h-3.5 w-3.5" />
          Assign to Policy
        </Button>
      </div>

      {(assignments ?? []).length === 0 ? (
        <p className="text-sm text-muted-foreground px-1">No policies assigned yet.</p>
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Policy Number</TableHead>
                <TableHead>Customer Name</TableHead>
                <TableHead>Assigned</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(assignments ?? []).map(a => {
                const policy = policyMap.get(a.policyId);
                return (
                  <TableRow key={a._id}>
                    <TableCell className="font-medium text-sm">
                      {policy?.policyNumber ?? a.policyId}
                    </TableCell>
                    <TableCell className="text-sm">{policy?.customerName ?? '—'}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {new Date(a.assignedAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-destructive hover:text-destructive"
                        onClick={() => requestUnassign(a._id)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog
        open={dialogOpen}
        onOpenChange={open => {
          if (!open) closeDialog();
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign to Policy</DialogTitle>
          </DialogHeader>
          <div className="py-2">
            <Popover open={comboboxOpen} onOpenChange={setComboboxOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={comboboxOpen}
                  aria-controls="assignment-combobox-list"
                  className={cn(
                    'w-full !h-10 justify-between font-normal',
                    !selectedPolicyId && 'text-muted-foreground'
                  )}
                >
                  {selectedDisplayText || 'Search policy…'}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-(--radix-popover-trigger-width) p-0" align="start">
                <Command>
                  <CommandInput placeholder="Search by number or name…" />
                  <CommandList id="assignment-combobox-list">
                    <CommandEmpty>No policies found.</CommandEmpty>
                    <CommandGroup>
                      {allPoliciesExceptCurrentPlan.map(p => {
                        const label = `${p.policyNumber}${p.customerName ? ` — ${p.customerName}` : ''}`;
                        return (
                          <CommandItem
                            key={p._id}
                            value={label}
                            onSelect={() => {
                              setSelectedPolicyId(p._id === selectedPolicyId ? '' : p._id);
                              setComboboxOpen(false);
                            }}
                          >
                            <Check
                              className={cn(
                                'mr-2 h-4 w-4',
                                selectedPolicyId === p._id ? 'opacity-100' : 'opacity-0'
                              )}
                            />
                            {label}
                          </CommandItem>
                        );
                      })}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            {allPoliciesExceptCurrentPlan.length === 0 && (
              <p className="mt-2 text-sm text-muted-foreground">No policies available to assign.</p>
            )}
            {hasExistingOtherPlan && (
              <div className="mt-3 rounded-md border border-orange-400 p-3 text-sm">
                <p className="font-medium text-orange-700 dark:text-orange-400">
                  This policy is currently assigned to: {existingAssignment.planName}
                </p>
                <p className="mt-1 text-muted-foreground">
                  Reassigning will remove the existing plan assignment and reset all utilization
                  data.
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeDialog}>
              Cancel
            </Button>
            <Button
              onClick={() => void handleAssign()}
              disabled={!selectedPolicyId || isAssigning}
              variant={hasExistingOtherPlan ? 'destructive' : 'default'}
            >
              {isAssigning ? 'Processing…' : hasExistingOtherPlan ? 'Reassign Plan' : 'Assign'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={confirmUnassignId !== null}
        onOpenChange={open => {
          if (!open) cancelUnassign();
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unassign this policy?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove the benefit plan assignment and delete all utilization data for this
              policy. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => void handleUnassign()}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Unassign
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
